import { create } from "zustand";
import { supabase } from "@/config/supabaseClient";
import { Session } from "@supabase/supabase-js";
import api from "@/config/axiosConfig";
import { User } from "@/types/user";
import { toast } from "@/hooks/use-toast";
import logger from "@/utils/logger";

interface AuthTokens {
  access_token: string;
  refresh_token: string;
  expiry: number;
}

interface UserStore {
  user: User | null;
  setUser: (user: User | null) => void;

  anonymousUser: boolean;
  setAnonymousUser: (isAnonymous: boolean) => void;
  accessToken: string | null;
  setAccessToken: (accessToken: string | null) => void;
  refreshToken: string | null;
  setRefreshToken: (refreshToken: string | null) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isSocialLoading: boolean;
  setIsSocialLoading: (isSocialLoading: boolean) => void;
  initialLoading: boolean;
  setInitialLoading: (isInitialLoading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  signIn: (email: string, password: string) => Promise<boolean>;
  signUp: (
    firstName: string,
    lastName: string,
    email: string,
    password: string
  ) => Promise<boolean>;
  logout: () => Promise<void>;
  profile_image: string | null;
  setProfileImage: (image: string | null) => void;
  background_image: string | null;
  setBackgroundImage: (image: string | null) => void;
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  // signInWithApple: () => Promise<void>;
  newAccount: boolean;
  setNewAccount: (newAccount: boolean) => void;
  clearAuth: () => void;
  handleAuthCallback: (
    session: Session | null,
    signIn: boolean,
    signUpMethod?: string
  ) => Promise<boolean>;
  initializeAuth: () => Promise<void>;
  initializeAnonymousUser: () => Promise<void>;
  isSocialAuthRedirect: boolean;
  setIsSocialAuthRedirect: (isRedirect: boolean) => void;
}

const useAuthStore = create<UserStore>((set, get) => ({
  user: null,
  anonymousUser: true,
  setAnonymousUser: (isAnonymous: boolean) => {
    set({ anonymousUser: isAnonymous });
  },
  accessToken: null,
  setAccessToken: (accessToken: string | null) => {
    set({ accessToken });
  },
  refreshToken: null,
  setRefreshToken: (refreshToken: string | null) => {
    set({ refreshToken });
  },
  isLoading: true,
  setIsLoading: (isLoading: boolean) => {
    set({ isLoading });
  },
  isSocialLoading: false,
  setIsSocialLoading: (isSocialLoading: boolean) => {
    set({ isSocialLoading });
  },
  initialLoading: true,
  setInitialLoading: (isInitialLoading: boolean) => {
    set({ initialLoading: isInitialLoading });
  },
  error: null,
  setError: (error: string | null) => {
    set({ error });
  },
  profile_image: null,
  setProfileImage: (image: string | null) => {
    set({ profile_image: image });
  },
  background_image: null,
  setBackgroundImage: (image: string | null) => {
    set({ background_image: image });
  },
  newAccount: false,
  setNewAccount: (newAccount: boolean) => {
    set({ newAccount });
  },
  isSocialAuthRedirect: false,
  clearAuth: () => set({ user: null, accessToken: null, refreshToken: null }),

  signIn: async (email: string, password: string) => {
    try {
      set({ isLoading: true });
      const { data: user, error: signInError } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });
      if (signInError) {
        throw signInError;
      }
      return true;
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === "Invalid login credentials"
      ) {
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: "Invalid email or password, please try again.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description:
            error instanceof Error ? error.message : "An error occurred",
        });
      }
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  signUp: async (
    firstName: string,
    lastName: string,
    email: string,
    password: string
  ) => {
    try {
      set({ isLoading: true });
      const { data: signUpData, error: signUpError } =
        await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              first_name: firstName,
              last_name: lastName,
              full_name: `${firstName} ${lastName}`,
            },
          },
        });

      if (signUpError) {
        if (signUpError.message === "User already registered") {
          toast({
            variant: "destructive",
            title: "Registration Error",
            description: "You have already signed up, please sign in.",
          });
          return false;
        }
        toast({
          variant: "destructive",
          title: "Registration Error",
          description: signUpError.message,
        });
        return false;
      }

      set({
        user: null,
        profile_image: null,
        anonymousUser: false,
      });

      toast({
        title: "Success",
        description: "Your account has been created successfully!",
      });
      return true;
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error ? error.message : "An error occurred",
      });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  logout: async () => {
    try {
      set({ isLoading: true });
      await supabase.auth.signOut();
      set({
        user: null,
        accessToken: null,
        refreshToken: null,
        profile_image: null,
      });
      toast({
        title: "Success",
        description: "You have been logged out successfully.",
      });
    } catch (error) {
      console.error("Logout failed:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to log out. Please try again.",
      });
    } finally {
      set({ isLoading: false });
    }
  },

  signInWithGoogle: async () => {
    try {
      set({ isSocialLoading: true, isSocialAuthRedirect: true });
      localStorage.setItem("socialAuthRedirect", "true");
      localStorage.setItem("signUpMethod", "google");
      logger.stage(
        "Signing in with Google, using redirect url: ",
        import.meta.env.VITE_REDIRECT_URL
      );
      await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: import.meta.env.VITE_REDIRECT_URL,
        },
      });
    } catch (error) {
      console.error("Error signing in with Google:", error);
      toast({
        variant: "destructive",
        title: "Google Sign-in Error",
        description: "Failed to sign in with Google. Please try again.",
      });
      localStorage.removeItem("socialAuthRedirect");
      localStorage.removeItem("signUpMethod");
      set({ isSocialLoading: false, isSocialAuthRedirect: false });
    }
  },

  signInWithFacebook: async () => {
    try {
      set({ isSocialLoading: true, isSocialAuthRedirect: true });
      localStorage.setItem("socialAuthRedirect", "true");
      localStorage.setItem("signUpMethod", "facebook");
      await supabase.auth.signInWithOAuth({
        provider: "facebook",
        options: {
          redirectTo: import.meta.env.VITE_REDIRECT_URL,
        },
      });
    } catch (error) {
      console.error("Error signing in with Facebook:", error);
      toast({
        variant: "destructive",
        title: "Facebook Sign-in Error",
        description: "Failed to sign in with Facebook. Please try again.",
      });
      localStorage.removeItem("socialAuthRedirect");
      localStorage.removeItem("signUpMethod");
      set({ isSocialLoading: false, isSocialAuthRedirect: false });
    }
  },

  // signInWithApple: async () => {
  //   try {
  //     set({ isSocialLoading: true, isSocialAuthRedirect: true });
  //     localStorage.setItem("socialAuthRedirect", "true");
  //     localStorage.setItem("signUpMethod", "apple");
  //     const redirectUrl = `${window.location.origin}/auth/callback`;
  //     await supabase.auth.signInWithOAuth({
  //       provider: "apple",
  //       options: {
  //         redirectTo: redirectUrl,
  //       },
  //     });
  //   } catch (error) {
  //     console.error("Error signing in with Apple:", error);
  //     toast({
  //       variant: "destructive",
  //       title: "Apple Sign-in Error",
  //       description: "Failed to sign in with Apple. Please try again.",
  //     });
  //     localStorage.removeItem("socialAuthRedirect");
  //     localStorage.removeItem("signUpMethod");
  //     set({ isSocialLoading: false, isSocialAuthRedirect: false });
  //   }
  // },

  setUser: (user: User | null) => {
    set({ user });
  },

  handleAuthCallback: async (
    session: Session | null,
    signIn: boolean = true,
    signUpMethod?: string
  ) => {
    if (!session) {
      set({ isLoading: false, initialLoading: false });
      return false;
    }

    logger.log(
      "[Auth] handleAuthCallback called with signIn:",
      signIn,
      session
    );

    try {
      set({ isLoading: true });

      if (!signIn) {
        const method =
          signUpMethod || localStorage.getItem("signUpMethod") || "email";

        const user = await api.auth.post("/user", {
          session: session,
          signUpMethod: method,
          ...(localStorage.getItem("anonymousUserId")
            ? { anon: localStorage.getItem("anonymousUserId") }
            : {}),
        });

        localStorage.removeItem("signUpMethod");

        logger.log(user.data);

        set({
          user: user.data,
          profile_image: user.data.profile_image,
          background_image: user.data.background_image,
          anonymousUser: false,
        });
        return true;
      }

      if (session.user.is_anonymous) {
        try {
          const existingUser = await api.auth.get("user");

          if (!existingUser.data) {
            const user = await api.auth.post("/user", {
              session: session,
              signUpMethod: "anonymous",
            });
            set({
              user: user.data,
              profile_image: user.data.profile_image,
              background_image: user.data.background_image,
            });
          } else {
            set({
              user: existingUser.data,
              profile_image: existingUser.data.profile_image,
              background_image: existingUser.data.background_image,
              anonymousUser: true,
            });
          }
          return true;
        } catch (error) {
          console.error("Error handling anonymous user:", error);
          set({
            user: {
              user_id: session.user.id,
              first_name: session.user.user_metadata.first_name ?? "",
              last_name: session.user.user_metadata.last_name ?? "",
              email: session.user.email ?? "",
              username: session.user.user_metadata.username ?? "",
              name: session.user.user_metadata.name ?? "",
              is_private: session.user.user_metadata.is_private ?? false,
              profile_image: session.user.user_metadata.profile_image ?? "",
              background_image:
                session.user.user_metadata.background_image ?? "",
            },
            anonymousUser: true,
          });
          return true;
        }
      }

      const user = await api.auth.get("user");

      if (!user.data) {
        set({
          user: null,
          profile_image: null,
          anonymousUser: true,
        });
        toast({
          variant: "destructive",
          title: "Error",
          description: "Hmm, something went wrong.",
        });
        return false;
      }

      set({
        user: user.data,
        profile_image: user.data.profile_image,
        background_image: user.data.background_image,
        anonymousUser: false,
      });
      return true;
    } catch (error) {
      console.error("Error in handleAuthCallback:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to authenticate. Please try again.",
      });
      return false;
    } finally {
      set({ isLoading: false, initialLoading: false });
    }
  },

  initializeAuth: async () => {
    try {
      logger.stage("[Auth] Initializing auth");
      set({ initialLoading: true });
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.error("Error checking session:", error);
        toast({
          variant: "destructive",
          title: "Session Error",
          description: "Failed to check authentication status.",
        });
        return;
      }

      logger.stage("[Auth] Session data:", session);

      if (session) {
        await get().handleAuthCallback(session, true);
      } else {
        await get().initializeAnonymousUser();
      }

      return;
    } catch (error) {
      console.error("Error initializing auth:", error);
      return;
    } finally {
      set({ initialLoading: false, isLoading: false });
    }
  },

  initializeAnonymousUser: async () => {
    const { data, error } = await supabase.auth.signInAnonymously();
    if (error) return;

    set({ initialLoading: false });

    const user = await api.auth.post("/user", {
      session: data,
      signUpMethod: "anonymous",
    });

    localStorage.setItem("anonymousUserId", user.data.user_id);

    set({
      user: user.data,
      profile_image: user.data.profile_image,
      anonymousUser: true,
    });
  },

  setIsSocialAuthRedirect: (isRedirect: boolean) => {
    if (!isRedirect) {
      localStorage.removeItem("socialAuthRedirect");
    }
    set({ isSocialAuthRedirect: isRedirect });
  },
}));

export default useAuthStore;
